import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import { Palette, Building2, ShieldCheck, RotateCcw } from 'lucide-react';
import { NS, ORG_SETTINGS_KEYS } from '../../../../../i18n/constants/keys';
import { Button } from '../../../../../ui';
import { TextArea } from '../../../../../ui/input/text_area/text_area';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import {
  getOrganization,
  updateOrganization,
} from '../../../../../utils/api_service/organization';
import { Organization } from '../../../../../utils/types/admin_types';
import LoadingSpinner from '../../../../job_openings/create/ui/LoaderSpinner';
import { UploadBase64Image } from './UploadBase64Image';
import { MotionColorPicker } from '../../../../../ui/components/ColorPicker/MotionColorPicker';
import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  applyBrandTheme,
  getContrastTextColor,
  getContrastRatio,
  hexToRgba,
  isValidHex,
  normalizeHex,
} from '../../../../../utils/theme/brandTheme';

export const NAVBAR_COLOR_PRESETS = [
  { name: 'Deep Forest (Default)', hex: '#093725' },
  { name: 'Midnight Navy', hex: '#0a192f' },
  { name: 'Space Slate', hex: '#1e293b' },
  { name: 'Deep Indigo', hex: '#1e1b4b' },
  { name: 'Deep Plum', hex: '#3b0764' },
  { name: 'Charcoal Black', hex: '#18181b' },
  { name: 'Clean White', hex: '#ffffff' },
  { name: 'Dark Teal', hex: '#042f2e' },
  { name: 'Dark Pine', hex: '#064e3b' },
  { name: 'Gunmetal Grey', hex: '#334155' },
];
export const SECONDARY_COLOR_PRESETS = NAVBAR_COLOR_PRESETS;

export const GeneralSettings = () => {
  const { t } = useTranslation(NS.organizationSettings);
  const [organization, setOrganization] = useState<Organization>({
    organizationName: '',
    supportEmail: '',
    replyEmail: '',
    supportPhone: '',
    companyLogo: '',
    perksAndBenefits: '',
    companyDetails: '',
    primaryColor: DEFAULT_PRIMARY_COLOR,
    secondaryColor: DEFAULT_SECONDARY_COLOR,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [confirmUpdate, setConfirmUpdate] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const response = await getOrganization();
        if (response.ok) {
          const data: Organization = await response.json();
          const primary =
            data.primaryColor ||
            data.brandColor ||
            localStorage.getItem('primaryColor') ||
            localStorage.getItem('brandPrimaryColor') ||
            DEFAULT_PRIMARY_COLOR;

          const secondary =
            data.secondaryColor ||
            localStorage.getItem('secondaryColor') ||
            localStorage.getItem('brandSecondaryColor') ||
            DEFAULT_SECONDARY_COLOR;

          const loadedOrg: Organization = {
            ...data,
            primaryColor: normalizeHex(primary, DEFAULT_PRIMARY_COLOR),
            secondaryColor: normalizeHex(secondary, DEFAULT_SECONDARY_COLOR),
          };

          setOrganization(loadedOrg);

          // Apply loaded theme to DOM and CSS variables
          applyBrandTheme({
            primaryColor: loadedOrg.primaryColor,
            secondaryColor: loadedOrg.secondaryColor,
          });
        }
      } catch (err) {
        console.error('Failed to load organization settings', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
  }, []);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setOrganization((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const setCompanyLogo = (base64Url: string) => {
    setOrganization((prevState) => ({
      ...prevState,
      companyLogo: base64Url,
    }));
  };

  const handlePrimaryColorChange = (newColor: string) => {
    setOrganization((prevState) => {
      const updated = {
        ...prevState,
        primaryColor: newColor,
        brandColor: newColor,
      };
      applyBrandTheme({
        primaryColor: newColor,
        secondaryColor: updated.secondaryColor,
      });
      return updated;
    });
  };

  const handleSecondaryColorChange = (newColor: string) => {
    setOrganization((prevState) => {
      const updated = {
        ...prevState,
        secondaryColor: newColor,
      };
      applyBrandTheme({
        primaryColor: updated.primaryColor,
        secondaryColor: newColor,
      });
      return updated;
    });
  };

  const isCustomColors =
    normalizeHex(organization.primaryColor || DEFAULT_PRIMARY_COLOR).toLowerCase() !==
      normalizeHex(DEFAULT_PRIMARY_COLOR).toLowerCase() ||
    normalizeHex(organization.secondaryColor || DEFAULT_SECONDARY_COLOR).toLowerCase() !==
      normalizeHex(DEFAULT_SECONDARY_COLOR).toLowerCase();

  const handleResetToDefaults = () => {
    if (!isEditing) return;
    setOrganization((prevState) => {
      const updated = {
        ...prevState,
        primaryColor: DEFAULT_PRIMARY_COLOR,
        secondaryColor: DEFAULT_SECONDARY_COLOR,
        brandColor: DEFAULT_PRIMARY_COLOR,
      };
      applyBrandTheme({
        primaryColor: DEFAULT_PRIMARY_COLOR,
        secondaryColor: DEFAULT_SECONDARY_COLOR,
      });
      return updated;
    });
    toast.info('Palette reset to HiAcuity defaults (Primary #038912 & Navbar #093725)', {
      position: 'bottom-center',
    });
  };

  const updateOrg = async () => {
    if (!confirmUpdate) {
      toast.warning(t(ORG_SETTINGS_KEYS.messages.confirmBeforeUpdate), {
        position: 'bottom-center',
      });
      return;
    }

    setSaving(true);
    const validPrimary = isValidHex(organization.primaryColor || '')
      ? normalizeHex(organization.primaryColor || '', DEFAULT_PRIMARY_COLOR)
      : DEFAULT_PRIMARY_COLOR;

    const validSecondary = isValidHex(organization.secondaryColor || '')
      ? normalizeHex(organization.secondaryColor || '', DEFAULT_SECONDARY_COLOR)
      : DEFAULT_SECONDARY_COLOR;

    // Immediately persist and apply theme locally
    if (organization.companyLogo) {
      localStorage.setItem('companyLogo', organization.companyLogo);
    }
    applyBrandTheme({
      primaryColor: validPrimary,
      secondaryColor: validSecondary,
    });

    const payload: Organization = {
      organizationName: organization.organizationName || '',
      supportEmail: organization.supportEmail || '',
      replyEmail: organization.replyEmail || '',
      supportPhone: organization.supportPhone || '',
      companyLogo: organization.companyLogo || '',
      perksAndBenefits: organization.perksAndBenefits || '',
      companyDetails: organization.companyDetails || '',
      primaryColor: validPrimary,
      secondaryColor: validSecondary,
      brandColor: validPrimary,
    };

    try {
      const response = await updateOrganization(payload);
      if (response.ok) {
        toast.success(t(ORG_SETTINGS_KEYS.messages.updateSuccess) || 'Organization updated successfully', {
          position: 'bottom-center',
        });
        setIsEditing(false);
        setConfirmUpdate(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.error || errorData.message || 'Failed to update organization', {
          position: 'bottom-center',
        });
      }
    } catch (err: any) {
      toast.error(err?.message || 'An unexpected error occurred', {
        position: 'bottom-center',
      });
    } finally {
      setSaving(false);
    }
  };

  const currentPrimaryColor = organization.primaryColor || DEFAULT_PRIMARY_COLOR;
  const currentSecondaryColor = organization.secondaryColor || DEFAULT_SECONDARY_COLOR;

  const primaryContrastText = getContrastTextColor(currentPrimaryColor);
  const secondaryContrastText = getContrastTextColor(currentSecondaryColor);

  const primaryWcag = getContrastRatio(currentPrimaryColor, primaryContrastText);
  const secondaryWcag = getContrastRatio(currentSecondaryColor, secondaryContrastText);

  return (
    <>
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', gap: 1.5 }}>
          <LoadingSpinner label='' />
          <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.disabled', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Loading Organization Profile...
          </Typography>
        </Box>
      ) : (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, pb: 10 }}
        >
          {/* Header Bar */}
          <Paper
            component={motion.div}
            layout
            elevation={0}
            variant='outlined'
            sx={{
              p: { xs: 3, sm: 3.5 },
              borderRadius: '24px',
              bgcolor: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(16px)',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, textAlign: 'left' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                <Typography variant='h5' sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.02em' }}>
                  {organization?.organizationName || 'Organization Profile'}
                </Typography>
                <Box
                  component={motion.div}
                  layout
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '9999px',
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    background: `linear-gradient(135deg, ${currentPrimaryColor} 0%, ${currentSecondaryColor} 100%)`,
                    color: '#ffffff',
                  }}
                >
                  <Box component='span' sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ffffff' }} />
                  <span>Dual Theme Active</span>
                </Box>
              </Box>
              <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: 640 }}>
                Manage company branding, dynamic primary and secondary color palettes, contact profiles, and organizational styling across HiAcuity.
              </Typography>
            </Box>

            {/* iOS-Style Mode Switch */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'rgba(241, 245, 249, 0.9)', p: 1, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary', pl: 1, userSelect: 'none' }}>
                {isEditing
                  ? t(ORG_SETTINGS_KEYS.actions.exitEditMode)
                  : t(ORG_SETTINGS_KEYS.actions.editOrganization)}
              </Typography>

              <Box
                component='button'
                type='button'
                role='switch'
                aria-checked={isEditing}
                onClick={() => setIsEditing(!isEditing)}
                sx={{
                  position: 'relative',
                  display: 'inline-flex',
                  height: 28,
                  width: 48,
                  alignItems: 'center',
                  borderRadius: '9999px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                  bgcolor: isEditing ? currentPrimaryColor : 'rgba(203, 213, 225, 1)',
                  p: 0.5,
                }}
              >
                <Box
                  component={motion.span}
                  layout
                  transition={{ type: 'spring', damping: 25, stiffness: 350 }}
                  sx={{
                    display: 'inline-block',
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    bgcolor: '#ffffff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    transform: isEditing ? 'translateX(20px)' : 'translateX(0px)',
                  }}
                />
              </Box>
            </Box>
          </Paper>

          {/* Main Grid Content */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
            {/* Left 2-Columns: Theming & Profile Details */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Brand Theme Customization Card */}
              <Paper
                component={motion.div}
                layout
                elevation={0}
                variant='outlined'
                sx={{
                  p: { xs: 3, sm: 3.5 },
                  borderRadius: '24px',
                  bgcolor: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(16px)',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                  position: 'relative',
                  zIndex: 30,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: '16px',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${currentPrimaryColor} 0%, ${currentSecondaryColor} 100%)`,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      }}
                    >
                      <Palette size={20} />
                    </Box>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant='subtitle1' sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
                        Brand Colors & Dynamic Theming
                      </Typography>
                      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                        Customize primary brand color and top navbar color using physics-based motion color wheels.
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isCustomColors && isEditing && (
                      <Box
                        component={motion.button}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type='button'
                        onClick={handleResetToDefaults}
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          bgcolor: '#093725',
                          color: '#ffffff',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          transition: 'all 0.15s ease',
                          '&:hover': { bgcolor: '#041d13' },
                        }}
                        title='Reset both primary and navbar colors to default'
                      >
                        <RotateCcw size={14} />
                        <span>Reset to Default Colors</span>
                      </Box>
                    )}
                    <Chip
                      label='PUT /organization'
                      size='small'
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.625rem',
                        height: 22,
                        bgcolor: 'rgba(9, 55, 37, 0.08)',
                        color: '#093725',
                        border: '1px solid rgba(9, 55, 37, 0.15)',
                      }}
                    />
                  </Box>
                </Box>

                {/* Dual Color Pickers: Primary & Secondary */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, position: 'relative', zIndex: 40 }}>
                  {/* Primary Color Picker */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, position: 'relative', zIndex: 30 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
                      <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Primary Color
                      </Typography>
                      <Typography variant='caption' sx={{ fontSize: '0.625rem', color: 'text.disabled', fontWeight: 600 }}>
                        Buttons, Accents, Links
                      </Typography>
                    </Box>
                    <MotionColorPicker
                      color={currentPrimaryColor}
                      onChange={handlePrimaryColorChange}
                      isEditing={isEditing}
                      label='Primary Color'
                      description='Main interactive accents & buttons'
                      defaultColor={DEFAULT_PRIMARY_COLOR}
                      popoverAlign='left'
                    />
                  </Box>

                  {/* Navbar Color Picker */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, position: 'relative', zIndex: 20 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
                      <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Navbar Color
                      </Typography>
                      <Typography variant='caption' sx={{ fontSize: '0.625rem', color: 'text.disabled', fontWeight: 600 }}>
                        Top Navigation Bar & Header
                      </Typography>
                    </Box>
                    <MotionColorPicker
                      color={currentSecondaryColor}
                      onChange={handleSecondaryColorChange}
                      isEditing={isEditing}
                      label='Navbar Color'
                      description='Top navigation bar & header background'
                      presetColors={NAVBAR_COLOR_PRESETS}
                      defaultColor={DEFAULT_SECONDARY_COLOR}
                      popoverAlign='right'
                    />
                  </Box>
                </Box>

                {/* Live Brand Theme Application Preview Box */}
                <Paper
                  variant='outlined'
                  sx={{
                    bgcolor: 'rgba(248, 250, 252, 0.9)',
                    borderRadius: '16px',
                    p: 2.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    borderColor: 'divider',
                    textAlign: 'left',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Live System Theme Preview
                      </Typography>
                      <Chip
                        label={isCustomColors ? 'Custom Brand Palette' : 'Default HiAcuity Palette'}
                        size='small'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.625rem',
                          height: 20,
                          bgcolor: isCustomColors ? 'rgba(245, 158, 11, 0.1)' : 'rgba(9, 55, 37, 0.08)',
                          color: isCustomColors ? '#b45309' : '#093725',
                          border: '1px solid',
                          borderColor: isCustomColors ? 'rgba(245, 158, 11, 0.2)' : 'rgba(9, 55, 37, 0.15)',
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontFamily: 'monospace', fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant='caption' sx={{ fontFamily: 'inherit', color: 'text.disabled', fontSize: '0.625rem' }}>Primary:</Typography>
                        <Box
                          component='span'
                          sx={{ width: 10, height: 10, borderRadius: '50%', display: 'inline-block', border: '1px solid rgba(0,0,0,0.1)', bgcolor: currentPrimaryColor }}
                        />
                        {currentPrimaryColor.toUpperCase()}
                      </Box>
                      <Box component='span' sx={{ color: 'text.disabled' }}>•</Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant='caption' sx={{ fontFamily: 'inherit', color: 'text.disabled', fontSize: '0.625rem' }}>Navbar:</Typography>
                        <Box
                          component='span'
                          sx={{ width: 10, height: 10, borderRadius: '50%', display: 'inline-block', border: '1px solid rgba(0,0,0,0.1)', bgcolor: currentSecondaryColor }}
                        />
                        {currentSecondaryColor.toUpperCase()}
                      </Box>
                    </Box>
                  </Box>

                  {/* Interactive UI Elements Preview */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                    {/* Primary Button Preview */}
                    <Box
                      component={motion.button}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      type='button'
                      sx={{
                        px: 2,
                        py: 1,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        borderRadius: '12px',
                        border: 'none',
                        cursor: 'pointer',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        bgcolor: currentPrimaryColor,
                        color: primaryContrastText,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <span>Primary Action</span>
                      <Typography component='span' sx={{ fontSize: '0.625rem', opacity: 0.8 }}>
                        ({primaryWcag.toFixed(1)}:1)
                      </Typography>
                    </Box>

                    {/* Top Navbar Preview Pill */}
                    <Box
                      component={motion.div}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      sx={{
                        px: 2,
                        py: 1,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        borderRadius: '12px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        bgcolor: currentSecondaryColor,
                        color: secondaryContrastText,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        border: '1px solid rgba(0,0,0,0.05)',
                      }}
                    >
                      <span>Top Navbar</span>
                      <Typography component='span' sx={{ fontSize: '0.625rem', opacity: 0.8 }}>
                        ({secondaryWcag.toFixed(1)}:1)
                      </Typography>
                    </Box>

                    {/* Gradient Theme Pill */}
                    <Box
                      component={motion.div}
                      whileHover={{ scale: 1.04 }}
                      sx={{
                        px: 2,
                        py: 1,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: '#ffffff',
                        borderRadius: '12px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        background: `linear-gradient(135deg, ${currentPrimaryColor} 0%, ${currentSecondaryColor} 100%)`,
                        userSelect: 'none',
                      }}
                    >
                      Brand Gradient Blend
                    </Box>

                    {/* Active Status Badge */}
                    <Chip
                      label='Active Status'
                      size='small'
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        bgcolor: hexToRgba(currentPrimaryColor, 0.12),
                        color: currentPrimaryColor,
                        border: `1px solid ${hexToRgba(currentPrimaryColor, 0.25)}`,
                      }}
                    />
                  </Box>
                </Paper>
              </Paper>

              {/* General Organization Details Card */}
              <Paper
                component={motion.div}
                layout
                elevation={0}
                variant='outlined'
                sx={{
                  p: { xs: 3, sm: 3.5 },
                  borderRadius: '24px',
                  bgcolor: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(16px)',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  position: 'relative',
                  zIndex: 10,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.25, borderRadius: '16px', bgcolor: 'rgba(241, 245, 249, 1)', color: 'text.primary', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Building2 size={20} />
                    </Box>
                    <Box>
                      <Typography variant='subtitle1' sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
                        General Information
                      </Typography>
                      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                        Company contact emails, address details, and support communication.
                      </Typography>
                    </Box>
                  </Box>

                  <Chip
                    label={isEditing ? 'Editing Mode' : 'Read Only'}
                    size='small'
                    sx={{ fontWeight: 700, fontSize: '0.6875rem' }}
                  />
                </Box>

                {isEditing ? (
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}
                  >
                    <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                      <TextInput
                        name='organizationName'
                        label={t(ORG_SETTINGS_KEYS.labels.companyName)}
                        value={organization?.organizationName || ''}
                        type='text'
                        onChange={handleChange}
                      />
                    </Box>
                    <TextInput
                      name='supportEmail'
                      label={t(ORG_SETTINGS_KEYS.labels.supportEmail)}
                      value={organization?.supportEmail || ''}
                      type='text'
                      onChange={handleChange}
                    />
                    <TextInput
                      name='replyEmail'
                      label={t(ORG_SETTINGS_KEYS.labels.replyToEmail)}
                      value={organization?.replyEmail || ''}
                      type='text'
                      onChange={handleChange}
                    />
                    <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                      <TextInput
                        name='supportPhone'
                        label={t(ORG_SETTINGS_KEYS.labels.supportPhone)}
                        value={organization?.supportPhone || ''}
                        type='text'
                        onChange={handleChange}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, textAlign: 'left' }}>
                    <Paper variant='outlined' sx={{ p: 2, borderRadius: '16px', bgcolor: 'rgba(248, 250, 252, 0.7)', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem' }}>
                        {t(ORG_SETTINGS_KEYS.labels.companyName)}
                      </Typography>
                      <Typography variant='body2' sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {organization?.organizationName || 'N/A'}
                      </Typography>
                    </Paper>

                    <Paper variant='outlined' sx={{ p: 2, borderRadius: '16px', bgcolor: 'rgba(248, 250, 252, 0.7)', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem' }}>
                        {t(ORG_SETTINGS_KEYS.labels.supportEmail)}
                      </Typography>
                      <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.primary', fontFamily: 'monospace' }}>
                        {organization?.supportEmail || 'N/A'}
                      </Typography>
                    </Paper>

                    <Paper variant='outlined' sx={{ p: 2, borderRadius: '16px', bgcolor: 'rgba(248, 250, 252, 0.7)', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem' }}>
                        {t(ORG_SETTINGS_KEYS.labels.replyToEmail)}
                      </Typography>
                      <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.primary', fontFamily: 'monospace' }}>
                        {organization?.replyEmail || 'N/A'}
                      </Typography>
                    </Paper>

                    <Paper variant='outlined' sx={{ p: 2, borderRadius: '16px', bgcolor: 'rgba(248, 250, 252, 0.7)', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem' }}>
                        {t(ORG_SETTINGS_KEYS.labels.supportPhone)}
                      </Typography>
                      <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {organization?.supportPhone || 'N/A'}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {/* Additional Text Areas */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider', textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.primary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t(ORG_SETTINGS_KEYS.labels.perksAndBenefits)}
                    </Typography>
                    {isEditing ? (
                      <TextArea
                        name='perksAndBenefits'
                        value={organization?.perksAndBenefits || ''}
                        onChange={handleChange}
                        rows={5}
                      />
                    ) : (
                      <Paper variant='outlined' sx={{ p: 2, borderRadius: '16px', bgcolor: 'rgba(248, 250, 252, 0.8)', borderColor: 'divider', fontSize: '0.75rem', color: 'text.primary', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                        {organization?.perksAndBenefits ||
                          t(ORG_SETTINGS_KEYS.empty.noPerks)}
                      </Paper>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.primary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t(ORG_SETTINGS_KEYS.labels.companyDetails)}
                    </Typography>
                    {isEditing ? (
                      <TextArea
                        name='companyDetails'
                        value={organization?.companyDetails || ''}
                        onChange={handleChange}
                        rows={4}
                      />
                    ) : (
                      <Paper variant='outlined' sx={{ p: 2, borderRadius: '16px', bgcolor: 'rgba(248, 250, 252, 0.8)', borderColor: 'divider', fontSize: '0.75rem', color: 'text.primary', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                        {organization?.companyDetails ||
                          t(ORG_SETTINGS_KEYS.empty.noCompanyDetails)}
                      </Paper>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Box>

            {/* Right Column: Logo Upload Box */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Paper
                component={motion.div}
                layout
                elevation={0}
                variant='outlined'
                sx={{
                  p: { xs: 3, sm: 3.5 },
                  borderRadius: '24px',
                  bgcolor: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(16px)',
                  borderColor: 'divider',
                  position: { lg: 'sticky' },
                  top: 24,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Box sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left' }}>
                  <Typography variant='subtitle2' sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Company Branding Logo
                  </Typography>
                  <ShieldCheck size={18} color='#093725' />
                </Box>

                <UploadBase64Image
                  logo={organization.companyLogo || ''}
                  setLogo={setCompanyLogo}
                  isEditing={isEditing}
                />
              </Paper>
            </Box>
          </Box>

          {/* Edit Mode Floating / Sticky Actions Drawer */}
          <AnimatePresence>
            {isEditing && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 30, scale: 0.96 }}
                transition={{ type: 'spring', damping: 25, stiffness: 320 }}
                sx={{
                  position: 'sticky',
                  bottom: 24,
                  zIndex: 40,
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(16px)',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '24px',
                  p: { xs: 2, sm: 2.5 },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  maxWidth: 896,
                  width: '100%',
                  mx: 'auto',
                  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmUpdate}
                      onChange={(e) => setConfirmUpdate(e.target.checked)}
                      sx={{
                        color: currentPrimaryColor,
                        '&.Mui-checked': {
                          color: currentPrimaryColor,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {t(ORG_SETTINGS_KEYS.labels.confirmUpdate)}
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: { xs: '100%', sm: 'auto' }, justifyContent: 'flex-end' }}>
                  <Button
                    label={t(ORG_SETTINGS_KEYS.actions.cancel)}
                    onClick={() => {
                      setIsEditing(false);
                      setConfirmUpdate(false);
                    }}
                    variant='secondary'
                    sx={{ '&:hover': { bgcolor: '#f8fafc' }, color: '#334155', borderRadius: '12px' }}
                  />
                  <Button
                    label={
                      saving
                        ? 'Saving...'
                        : t(ORG_SETTINGS_KEYS.actions.updateOrganization)
                    }
                    onClick={updateOrg}
                    variant='primary'
                    disabled={!confirmUpdate || saving}
                    sx={{
                      borderRadius: '12px',
                      transition: 'all 0.2s ease',
                      boxShadow: confirmUpdate && !saving ? 2 : 'none',
                      opacity: confirmUpdate && !saving ? 1 : 0.5,
                    }}
                  />
                </Box>
              </Box>
            )}
          </AnimatePresence>
        </Box>
      )}
    </>
  );
};

